//
// AccountWorkspace
//
// Manage account settings and manage projects

import { observer } from 'mobx-react-lite'
import { usePersistentState } from '../hooks/usePersistentState'
import { useTooltipPopover } from '../hooks/useTooltipPopover'
import { useStore } from '../stores'

import { HView, Spacer, VScrollView, VView } from '../appview'
import { Bar, Button, Icon, Item } from '../components'
import { Avatar, DeleteAccountPanel } from '../panels'
import { WorkspaceView } from './WorkspaceView'

import { DeleteAccountDisabledPanel } from '../panels/DeleteAccountDisabledPanel'
import { AccountSettingsWorksheet, ProjectManagementWorksheet } from './account'

export const AccountWorkspace = observer(function AccountWorkspace({
    viewkey,
    grow,
    ...props
}) {
    const store = useStore()
    const { app, view, data } = store

    const userProjects = data.projectsForUser(view.user.gid)
    const ownedProject = userProjects.find(projectMemberships => {
        return projectMemberships.roles.includes('owner')
    })
    const isProjectOwner = ownedProject != null

    const DeleteAccountDisabledPopover = useTooltipPopover(DeleteAccountDisabledPanel, {
        preferredPosition: 'top',
    })

    const DeleteAccountPopover = useTooltipPopover(DeleteAccountPanel, {
        preferredPosition: 'right',
    })
    const onCancelDeleteAccount = () => {
        DeleteAccountPopover.hide()
    }
    const onConfirmDeleteAccount = confirm => {
        DeleteAccountPopover.hide()
        // actually delete the account!
        store.deleteAccount(confirm)
    }

    const Popover = isProjectOwner ? DeleteAccountDisabledPopover : DeleteAccountPopover

    // the ui
    const [navigatorItem, setNavigatorItem] = usePersistentState(
        viewkey + '/navigatorItem',
        'account'
    )

    const Navigator = (
        <VView grow>
            <Item
                indent={0}
                selected={navigatorItem === 'account'}
                onClick={e => {
                    setNavigatorItem('account')
                }}
            >
                <Avatar user={view.user} />
                {view.user.displayname}
            </Item>
            <Item
                indent={0}
                selected={navigatorItem === 'projects'}
                onClick={e => {
                    setNavigatorItem('projects')
                }}
            >
                <Icon name="product" size={4} />
                {app.text('Project management')}
            </Item>
            <Spacer size={20} />
            <HView grow>
                <Spacer size={40} />
                <VView grow>
                    <Bar raised>
                        <Button
                            action
                            onClick={() => {
                                store.logout()
                            }}
                        >
                            {app.text('Log out')}
                        </Button>
                    </Bar>
                    <Spacer grow />
                    <DeleteAccountPopover.Panel
                        onConfirm={onConfirmDeleteAccount}
                        onCancel={onCancelDeleteAccount}
                    />
                    <DeleteAccountDisabledPopover.Panel />
                    <Bar raised>
                        <Button
                            action
                            ref={Popover.anchorRef}
                            onClick={e => {
                                !isProjectOwner && Popover.onShow(e)
                            }}
                            onMouseEnter={e => {
                                isProjectOwner && Popover.onShow(e)
                            }}
                            onMouseLeave={e => {
                                isProjectOwner && Popover.hide()
                            }}
                            disabled={isProjectOwner}
                        >
                            {app.text('Delete account')}
                        </Button>
                    </Bar>
                </VView>
                <Spacer size={40} />
            </HView>
            <Spacer size={20} />
        </VView>
    )

    const worksheet =
        navigatorItem === 'account' ? (
            <AccountSettingsWorksheet user={view.user} />
        ) : (
            <ProjectManagementWorksheet user={view.user} />
        )

    const Inspector =
        navigatorItem === 'account' ? <VView grow></VView> : <VView grow></VView>

    const NavigatorView = Navigator
    const WorksheetView = <VScrollView>{worksheet}</VScrollView>
    const InspectorView = Inspector

    return (
        <WorkspaceView
            grow={grow}
            className="workspace-account"
            viewkey={viewkey}
            minWorksheetWidth={640}
            navigator={NavigatorView}
            worksheet={WorksheetView}
            inspector={InspectorView}
        />
    )
})
